/**
 * 日落橙模版 - 首页
 * 
 * 设计理念：搜索优先极简布局 + 杂志式排版 + 瀑布流
 * 与经典橙色（Hero大图+卡片网格+列表式）完全不同的排版骨架：
 * - 顶部：极简统计条（无大富Hero）
 * - 搜索：大号搜索框占据视觉焦点
 * - 分类：横向滚动图标条（非网格/气泡）
 * - 置顶：横向滚动全宽大卡片
 * - 最新：瀑布流（masonry）2列布局（卡片高度不一）
 * - 底部：3个快捷工具卡片
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/templates/sunset/components/Navbar";
import SunsetFooter from "@/templates/sunset/components/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function SunsetHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [totalListings, setTotalListings] = useState(0);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 12)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 16 })
      .then((data) => {
        setListings(data.list || []);
        setTotalListings(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  const pinnedListings = listings.filter(l => l.is_pinned);
  const normalListings = listings.filter(l => !l.is_pinned);

  return (
    <div className="min-h-screen bg-[#fffbf5]">
      <Navbar />

      {/* ======== 第一部分：大号搜索框（视觉焦点）======== */}
      <section className="pt-24 pb-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
              找到你需要的<span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent">热门服务</span>
            </h1>
            <p className="text-gray-400 mt-2 text-sm sm:text-base">便捷发布 · 一键搜索 · 快速触达</p>
          </div>

          <form onSubmit={handleSearch}>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl blur-lg opacity-25 group-hover:opacity-40 transition-opacity" />
              <div className="relative flex items-center bg-white rounded-2xl border-2 border-amber-200 group-hover:border-amber-400 transition-colors shadow-xl shadow-amber-500/10 overflow-hidden">
                <div className="flex-1 flex items-center pl-5">
                  <i className="ri-search-line text-amber-400 text-xl mr-3" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索你需要的服务..."
                    className="flex-1 py-4 sm:py-5 text-base text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent"
                  />
                </div>
                <button
                  type="submit"
                  className="shrink-0 px-8 py-4 sm:py-5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold hover:from-amber-600 hover:to-orange-700 transition-all text-sm sm:text-base"
                >
                  <i className="ri-search-line mr-1.5" />搜索
                </button>
              </div>
              {showSearchTip && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 px-4 py-2.5 bg-amber-500 text-white text-sm rounded-xl shadow-lg whitespace-nowrap">
                  <i className="ri-error-warning-line mr-1" />{searchConfig.disabledMessage || "搜索功能已关闭"}
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* ======== 第二部分：分类 网格平均分布 ======== */}
      <section className="pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">服务分类</h2>
            <Link to="/category" className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1">
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingCategories ? (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse aspect-square bg-gray-100 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className={`grid gap-3 ${
              categories.length <= 3 ? 'grid-cols-3' :
              categories.length <= 4 ? 'grid-cols-4' :
              categories.length <= 6 ? 'grid-cols-3 sm:grid-cols-6' :
              'grid-cols-4 sm:grid-cols-6 lg:grid-cols-8'
            }`}>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center justify-center gap-2 p-3 sm:p-4 bg-white rounded-2xl border border-gray-100 hover:border-amber-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300 aspect-square"
                >
                  <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-amber-50 flex items-center justify-center text-amber-500">
                    <i className={`${cat.icon || 'ri-folder-line'} text-lg sm:text-xl`} />
                  </div>
                  <span className="text-[11px] sm:text-xs text-gray-600 font-medium text-center line-clamp-1">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ======== 第三部分：置顶推荐 杂志布局（1大+2小）======== */}
      {!loadingListings && pinnedListings.length > 0 && (
        <section className="pb-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 mb-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 bg-amber-500 text-white rounded-full">
                <i className="ri-pushpin-line" />置顶推荐
              </span>
              <span className="text-lg font-bold text-gray-800">精选服务</span>
            </div>

            {/* 杂志式布局 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* 第1个置顶：大卡片（占满第一行左列 + 第二行跨2列） */}
              {pinnedListings[0] && (
                <Link
                  to={`/listing/${pinnedListings[0].id}`}
                  className="sm:col-span-2 group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:border-amber-200 transition-all"
                >
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-1/2 aspect-square sm:aspect-square bg-amber-50 overflow-hidden">
                      {pinnedListings[0].images?.[0] ? (
                        <img src={pinnedListings[0].images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-amber-300 text-4xl" />
                        </div>
                      )}
                    </div>
                    <div className="sm:w-1/2 p-5 sm:p-8 flex flex-col justify-center">
                      <span className="text-xs text-amber-600 font-semibold uppercase tracking-wide mb-2">
                        {pinnedListings[0].category?.name || "精选推荐"}
                      </span>
                      <h3 className="text-xl font-bold text-gray-800 group-hover:text-amber-600 transition-colors mb-3 line-clamp-2">
                        {pinnedListings[0].title}
                      </h3>
                      <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed mb-4">
                        {pinnedListings[0].content?.substring(0, 120) || "暂无描述"}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><i className="ri-time-line" />{formatDate(pinnedListings[0].created_at)}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line" />{pinnedListings[0].view_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* 第2、3个置顶：小卡片各占一列 */}
              {pinnedListings.slice(1, 3).map((item) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-amber-200 transition-all"
                >
                  <div className="aspect-square bg-amber-50 overflow-hidden">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-amber-300 text-3xl" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 group-hover:text-amber-600 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                      <span>{item.category?.name || "未分类"}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line" />{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                </Link>
              ))}

              {/* 第4+个置顶：小缩略图横条 */}
              {pinnedListings.slice(3).map((item) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="sm:col-span-2 group flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-amber-200 hover:shadow-sm transition-all"
                >
                  <div className="w-16 h-16 rounded-xl bg-amber-50 overflow-hidden shrink-0">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-amber-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 group-hover:text-amber-600 transition-colors">{item.title}</h3>
                    <p className="text-xs text-gray-400 mt-1">
                      {item.category?.name} · {formatDate(item.created_at)}
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-gray-300 group-hover:text-amber-500" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ======== 第四部分：最新信息 瀑布流Masonry布局 ======== */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">最新发布</h2>
            <Link to="/category" className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1">
              浏览全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="columns-1 sm:columns-2 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="break-inside-avoid animate-pulse bg-white rounded-2xl border border-gray-100 overflow-hidden mb-4">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="w-3/4 h-4 bg-gray-200 rounded" />
                    <div className="w-1/2 h-3 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : normalListings.length > 0 ? (
            <div className="columns-1 sm:columns-2 gap-4">
              {normalListings.slice(0, 10).map((item) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="break-inside-avoid block mb-4 group"
                >
                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-amber-200 transition-all">
                    <div className="aspect-square overflow-hidden">
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full bg-amber-50/50 flex items-center justify-center">
                          <i className="ri-image-line text-amber-300 text-3xl" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-amber-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-2 line-clamp-2 leading-relaxed">
                        {item.content?.substring(0, 60) || "暂无描述"}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium">
                          {item.category?.name || "未分类"}
                        </span>
                        <span className="text-[11px] text-gray-400">{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-amber-50">
                <i className="ri-inbox-line text-2xl text-amber-400" />
              </div>
              <p className="text-gray-500 font-medium">暂无信息</p>
              <p className="text-gray-400 text-sm mt-1">快来发布第一条信息吧</p>
            </div>
          )}
        </div>
      </section>

      {/* ======== 第五部分：快捷工具卡片 ======== */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 gap-4">
            <Link
              to="/publish"
              className="group flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100 hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform">
                <i className="ri-add-line text-white text-2xl" />
              </div>
              <div className="text-center">
                <span className="text-sm font-bold text-gray-800">发布信息</span>
                <p className="text-[11px] text-gray-400 mt-0.5">快速发布</p>
              </div>
            </Link>

            <Link
              to="/category"
              className="group flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-100 hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform">
                <i className="ri-grid-line text-white text-2xl" />
              </div>
              <div className="text-center">
                <span className="text-sm font-bold text-gray-800">浏览分类</span>
                <p className="text-[11px] text-gray-400 mt-0.5">发现服务</p>
              </div>
            </Link>
          </div>

          {/* 列表为空时的发布引导 */}
          {!loadingListings && listings.length === 0 && (
            <div className="text-center mt-10">
              <Link
                to="/publish"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-2xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg shadow-amber-500/30"
              >
                <i className="ri-add-line text-lg" />立即发布第一条信息
              </Link>
            </div>
          )}
        </div>
      </section>

      <SunsetFooter />
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
